import { Entity } from '@brochington/ecstatic';
import AppStoreContext from 'contexts/AppStoreContext';
import ECSMapContext from 'contexts/ECSMapContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import MarkupKitContext from 'contexts/MarkupKitContext';
import { Components } from 'editor/components';
import Markup from 'editor/components/Markup';
import MarkupKit from 'editor/components/MarkupKit';
import Transform from 'editor/components/Transform';
import React, { FC, useContext, useEffect, useState } from 'react';
import { CreatePunch } from 'records/interactions/CreatePunch';
import { PostRFI } from 'records/interactions/PostRFI';
import { Markup as MarkupRecord } from 'records/Markup';
import { useProjectManager } from 'providers/ProjectManagerProvider';
import MarkupMarkerPortal from './MarkupMarkerPortal';
import RemoveBtnMarker from './MarkupMarkers/RemoveBtnMarker';

type Props = {
  markup: MarkupRecord;
  transform: Transform;
};

const ECSMapSheetMarkup: FC<Props> = (props: Props) => {
  const { markup, transform } = props;
  const { markupsOverlay, markupsOverlayCanvas } = useContext(ECSMapContext);
  const world = useContext(ECSWorldContext);
  const markupKit = useContext(MarkupKitContext);
  const appStore = useContext(AppStoreContext);
  const [markupEntity, setMarkupEntity] = useState<Entity<Components> | null>(
    null
  );
  const [markupComp, setMarkupComp] = useState<Markup | null>(null);
  const projectManager = useProjectManager();
  const project = projectManager.getSelectedProject();
  const canDeleteRFIPermission = project.permissions.createRfi;

  useEffect(() => {
    const { rfis, punches, interactions } = appStore.state;

    const _markupComp = new Markup(markup);
    _markupComp.selected = false;

    const rfi = rfis.find((r) => r.postedmarkupids.contains(markup.markupid));

    if (rfi) {
      // Markups associated to OPEN rfis shouldn't exist at the moment.
      // short circuiting here to prevent rendering of open rfi markups.
      // unless the RFI has a previous version.
      if (rfi.answer === null && !rfi.previousversionid) {
        return;
      }

      _markupComp.isRFI = true;
    }

    if (punches.find((p) => p.markupids.contains(markup.markupid))) {
      _markupComp.isPunch = true;
    }

    const createPunchInteraction = interactions.find(
      (interaction) => interaction.type === 'CreatePunch'
    );

    if (createPunchInteraction) {
      const createdPunch = createPunchInteraction as CreatePunch;
      const interactionHasMarkup = createdPunch.punch.markups.some(
        (m) => m.clientGeneratedID === markup.markupid
      );
      if (interactionHasMarkup) {
        _markupComp.selected = true;
        _markupComp.isPunch = true;
        _markupComp.isNewPunch = true;
      }
    }

    const _markupEntity = world
      .createEntity()
      .add(_markupComp)
      .add(markupKit as MarkupKit)
      .add(markupsOverlayCanvas)
      .add(markupsOverlay)
      .add(transform)
      .addTag('markupsLayerMarkup')
      .addTag(`markup-on-${markup.fileid}`);

    setMarkupEntity(_markupEntity);
    setMarkupComp(_markupComp);
  }, []);

  useEffect(() => {
    if (!markupEntity) return;
    const { rfis, punches, interactions } = appStore.state;

    const _markupComp = markupEntity.get(Markup);

    if (_markupComp.markup !== markup) {
      _markupComp.markup = markup;
      _markupComp.mkMarkup = null; // Will be recreated in renderMarkup system;

      const maybeRFI = rfis.find((r) =>
        r.postedmarkupids.contains(markup.markupid)
      );

      if (maybeRFI) {
        _markupComp.isRFI = true;
      }

      const isPunch = (): boolean => {
        const maybePunch = punches.find((p) =>
          p.markupids.contains(markup.markupid)
        );
        if (!maybePunch) return false;

        const createPunchInteraction = interactions.find(
          (interaction) => interaction.type === 'CreatePunch'
        );
        if (!createPunchInteraction) return false;
        const createdPunch = createPunchInteraction as CreatePunch;
        const interactionHasMarkup = createdPunch.punch.markups.some(
          (m) => m.clientGeneratedID === markup.markupid
        );
        if (interactionHasMarkup) {
          _markupComp.selected = true;
        }

        return interactionHasMarkup;
      };

      if (isPunch()) {
        _markupComp.isPunch = true;
      }
    }
  }, [markupEntity, markup, transform]);

  useEffect(() => {
    return () => {
      if (markupEntity) {
        markupEntity.destroyImmediately();
      }
    };
  }, [markupEntity]);

  function handleRemoveClick() {
    const hasRfiPostInteractions = appStore.state.interactions.some(
      (i) => i.type === 'PostRFI'
    );

    if (hasRfiPostInteractions && markupComp?.markup) {
      const postRFI = appStore.state.interactions.find(
        (i) => i.type === 'PostRFI'
      ) as PostRFI;

      const postRFIWithoutMarkup = postRFI
        .delete('markup')
        .set('removingMarkup', true);
      appStore.actions.removeLocalMarkup(markupComp?.markup);
      appStore.actions.updateInteraction(postRFIWithoutMarkup);
    }
  }

  const hasRfiPostInteractions = appStore.state.interactions.some(
    (i) => i.type === 'PostRFI'
  );

  if (hasRfiPostInteractions) {
    const postRFI = appStore.state.interactions.find(
      (i) => i.type === 'PostRFI'
    ) as PostRFI;

    if (postRFI.markup?.markupid === markup.markupid) {
      return markupComp ? (
        <MarkupMarkerPortal markupComp={markupComp}>
          {canDeleteRFIPermission ? (
            <RemoveBtnMarker
              id="rfi-remove-marker"
              onRemoveClick={handleRemoveClick}
            />
          ) : null}
        </MarkupMarkerPortal>
      ) : null;
    }
  }

  return null;
};

export default ECSMapSheetMarkup;
